import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp, Calendar, ArrowLeft, Euro, Info } from 'lucide-react';
import AppinesBanner from './AppinesBanner';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';
import { trackPageView } from '../utils/googleAnalytics';
import { trackRevalorisationsPageViewed } from '../utils/analyticsEvents';
import { updatePageMeta } from '../utils/seoUtils';
import { REVALORIZATION_DATES } from '../constants/dates';

interface RevalorisationsPageProps {
  darkMode: boolean;
}

interface RevalorisationInfo {
  year: string;
  title: string;
  description: string;
  dateEntreeVigueur: string;
  isActive: boolean;
  principauxChangements: string[];
  impactEstime: string;
  actesModifies: string[];
  keywords: string;
}

const REVALORISATION_INFO: Record<string, RevalorisationInfo> = {
  '2025': {
    year: '2025',
    title: 'Revalorisations NGAP 2025 - Kinésithérapie',
    description: 'Découvrez les revalorisations des cotations de kinésithérapie prévues pour 2025. Augmentations tarifaires, nouveaux actes et modifications du référentiel NGAP.',
    dateEntreeVigueur: '1er juillet 2025',
    isActive: new Date() >= REVALORIZATION_DATES.JULY_2025,
    keywords: 'revalorisation 2025, NGAP 2025, augmentation tarifs kinésithérapie, cotations 2025',
    principauxChangements: [
      'Revalorisation des actes de rééducation active (RAM, RAO) avec une augmentation moyenne de 5 à 8%',
      'Augmentation significative des actes de drainage lymphatique (DRA) pour mieux valoriser cette expertise',
      'Amélioration de la prise en charge des actes de rééducation neurologique (NMI)',
      'Revalorisation des actes de rééducation périnéale et abdominale (RAB)',
      'Adaptation des coefficients pour les visites à domicile complexes'
    ],
    impactEstime: 'Ces revalorisations représentent une augmentation moyenne de 6% des revenus pour les kinésithérapeutes pratiquant majoritairement des actes de rééducation active. L\'impact varie selon les types d\'actes pratiqués.',
    actesModifies: [
      'RAM - Rééducation Analytique et Mobilisation',
      'RAO - Rééducation Active Orthopédique',
      'DRA - Drainage lymphatique',
      'NMI - Rééducation Neurologique',
      'RAB - Rééducation Abdominale',
      'VIC - Visites Complexes'
    ]
  },
  '2026': {
    year: '2026',
    title: 'Revalorisations NGAP 2026 - Kinésithérapie',
    description: 'Calendrier complet des revalorisations 2026 des actes de kinésithérapie. Deux vagues successives de revalorisation : janvier et 28 mai 2026 (Avenant 8), puis septembre pour certains actes spécifiques.',
    dateEntreeVigueur: '1er janvier 2026 et 28 mai 2026',
    isActive: new Date() >= REVALORIZATION_DATES.JANV_2026,
    keywords: 'revalorisation 2026, NGAP 2026, tarifs kinésithérapie 2026, cotations janvier 2026, mai 2026, avenant 8',
    principauxChangements: [
      'Première vague (janvier 2026) : Revalorisation ciblée des actes de rééducation des membres (RIM, RIC) avec augmentation de 7 à 10%',
      'Deuxième vague (28 mai 2026 - Avenant 8) : Extension de la revalorisation à l\'ensemble des actes de massage (AMK, AMS, AMC)',
      'Vague complémentaire (septembre 2026) : Ajustements spécifiques pour certains actes spécialisés',
      'Harmonisation des tarifs entre métropole et DROM pour certains actes',
      'Nouvelle grille de cotation pour les actes de rééducation respiratoire',
      'Revalorisation significative des actes de bilan (TER) pour valoriser l\'expertise diagnostique'
    ],
    impactEstime: 'Ces revalorisations échelonnées visent une augmentation globale de 8 à 12% des revenus moyens des kinésithérapeutes sur l\'année 2026. L\'impact sera progressif selon le calendrier d\'entrée en vigueur.',
    actesModifies: [
      'Janvier 2026 : RIM, RIC, VIM, VIC, RSM, RSC',
      '28 mai 2026 (Avenant 8) : AMK, AMS, AMC, RAM, RAO, DRA',
      'Septembre 2026 : Actes spécialisés (RPE, PLL, NMI)',
      'TER - Bilans kinésithérapiques',
      'Ensemble des actes de visite à domicile'
    ]
  },
  '2027': {
    year: '2027',
    title: 'Revalorisations NGAP 2027 - Kinésithérapie',
    description: 'Perspectives et revalorisations prévues pour 2027 des actes de kinésithérapie. Poursuite de la revalorisation progressive engagée en 2025-2026 avec focus sur les actes techniques complexes.',
    dateEntreeVigueur: '1er juillet 2027',
    isActive: new Date() >= REVALORIZATION_DATES.JULY_2027,
    keywords: 'revalorisation 2027, NGAP 2027, tarifs kinésithérapie 2027, cotations juillet 2027',
    principauxChangements: [
      'Finalisation du cycle de revalorisation 2025-2027 avec ajustement de tous les actes non encore revalorisés',
      'Mise en place d\'une nouvelle cotation pour les actes de télésoin en kinésithérapie',
      'Revalorisation majeure des actes de rééducation complexe (RIC, VIC, NMI avancé)',
      'Introduction de nouveaux actes pour la prise en charge des pathologies chroniques',
      'Valorisation accrue des actes de coordination avec les autres professionnels de santé',
      'Ajustement des indemnités kilométriques pour les déplacements en zone rurale'
    ],
    impactEstime: 'La revalorisation 2027 complète le cycle pluriannuel et devrait apporter une augmentation supplémentaire de 4 à 6% par rapport à 2026, portant l\'augmentation cumulée 2025-2027 à environ 18-23% selon les profils de pratique.',
    actesModifies: [
      'Tous les actes de rééducation complexe (coefficient +10 à +15%)',
      'NMI - Actes neurologiques avancés',
      'Nouveaux actes de télésoin kinésithérapique',
      'Actes de coordination pluriprofessionnelle',
      'Indemnités forfaitaires de déplacement majorées'
    ]
  }
};

const RevalorisationsPage: React.FC<RevalorisationsPageProps> = ({ darkMode }) => {
  const { year } = useParams<{ year: string }>();
  const navigate = useNavigate();
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false);

  const revaloInfo = year ? REVALORISATION_INFO[year] : null;

  useEffect(() => {
    if (revaloInfo) {
      updatePageMeta(undefined, {
        title: `${revaloInfo.title} | NGAP Kiné`,
        description: revaloInfo.description,
        keywords: revaloInfo.keywords,
        canonical: `https://ngap-kine.fr/revalorisations/${year}`,
        ogTitle: revaloInfo.title,
        ogDescription: revaloInfo.description,
        ogUrl: `https://ngap-kine.fr/revalorisations/${year}`,
        twitterTitle: revaloInfo.title,
        twitterDescription: revaloInfo.description
      });

      if (!hasTrackedPageView) {
        trackPageView(revaloInfo.title, `/revalorisations/${year}`);
        if (year) trackRevalorisationsPageViewed(year);
        setHasTrackedPageView(true);
      }
    }
  }, [revaloInfo, year, hasTrackedPageView]);

  if (!revaloInfo) {
    return (
      <div className="min-h-screen transition-colors duration-300" style={{
        background: darkMode
          ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <AppinesBanner className="md:pt-6" />
        <div className="pt-20 md:pt-0 px-4 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className={`text-center py-12 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            } shadow-lg`}>
              <div className={`text-6xl mb-4 ${
                darkMode ? 'text-orange-400' : 'text-orange-500'
              }`}>
                📊
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Année non disponible
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Les informations de revalorisation pour cette année ne sont pas encore disponibles.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/revalorisations/2025')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Revalorisations 2025
                </button>
                <button
                  onClick={() => navigate('/revalorisations/2026')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Revalorisations 2026
                </button>
                <button
                  onClick={() => navigate('/revalorisations/2027')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Revalorisations 2027
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Accueil', url: '/' },
    { label: 'Revalorisations', url: '/' },
    { label: `Revalorisations ${revaloInfo.year}`, url: `/revalorisations/${year}` }
  ];

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <AppinesBanner className="md:pt-6" />

      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb items={breadcrumbItems} darkMode={darkMode} />

          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className={`mr-4 p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-label="Retour"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center">
              <TrendingUp className={`h-8 w-8 mr-3 ${
                darkMode ? 'text-green-400' : 'text-green-600'
              }`} />
              <div>
                <h1 className={`text-3xl md:text-4xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Revalorisations {revaloInfo.year}
                </h1>
                <div className="flex items-center mt-2">
                  {revaloInfo.isActive ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <Calendar className="h-4 w-4 mr-1" />
                      En vigueur
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                      <Calendar className="h-4 w-4 mr-1" />
                      À venir
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={`mb-8 p-6 rounded-lg ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-lg`}>
            <p className={`text-lg leading-relaxed mb-4 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {revaloInfo.description}
            </p>
            <div className={`flex items-center text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <Calendar className="h-4 w-4 mr-2" />
              <span>Date d'entrée en vigueur : <strong>{revaloInfo.dateEntreeVigueur}</strong></span>
            </div>
          </div>

          <div className="space-y-6">
            <div className={`p-6 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            } shadow-lg`}>
              <div className="flex items-center mb-4">
                <Info className={`h-6 w-6 mr-3 ${
                  darkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
                <h2 className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Principaux changements
                </h2>
              </div>
              <ul className="space-y-3">
                {revaloInfo.principauxChangements.map((changement, index) => (
                  <li
                    key={index}
                    className={`flex items-start ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    <span className="mr-3 mt-1 text-green-500">✓</span>
                    <span className="flex-1">{changement}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-6 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            } shadow-lg`}>
              <div className="flex items-center mb-4">
                <Euro className={`h-6 w-6 mr-3 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`} />
                <h2 className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Impact estimé
                </h2>
              </div>
              <p className={`text-base leading-relaxed ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {revaloInfo.impactEstime}
              </p>
            </div>

            <div className={`p-6 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            } shadow-lg`}>
              <div className="flex items-center mb-4">
                <TrendingUp className={`h-6 w-6 mr-3 ${
                  darkMode ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <h2 className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Actes concernés
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {revaloInfo.actesModifies.map((acte, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded border-l-4 border-purple-400 ${
                      darkMode ? 'bg-purple-900/20' : 'bg-purple-50'
                    }`}
                  >
                    <span className={`text-sm ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {acte}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={`mt-8 p-6 rounded-lg border-l-4 border-orange-400 ${
            darkMode ? 'bg-orange-900/20' : 'bg-orange-50'
          }`}>
            <p className={`text-sm ${
              darkMode ? 'text-orange-200' : 'text-orange-800'
            }`}>
              <strong>Note importante :</strong> Les informations présentées sur cette page sont basées sur les annonces officielles et peuvent être sujettes à modifications.
              Pour connaître les tarifs exacts et les conditions d'application, consultez régulièrement le site de l'Assurance Maladie (Ameli.fr)
              et les communications de votre CPAM.
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {['2025', '2026', '2027'].filter(y => y !== year).map(y => (
              <button
                key={y}
                onClick={() => navigate(`/revalorisations/${y}`)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Voir les revalorisations {y}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RevalorisationsPage;
